import React from 'react';
import { Button, Textarea, Card } from '@parsina/framework';
import { Mic, MicOff, Sparkles } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { SOAP_SECTIONS } from '../../screens/Visit/Visit.shared.js';
import { CostChip } from '../common/ParsehCost.jsx';
import { useDictation } from './dictation.js';

// یادداشت — the SOAP note, in the ONE shape this app stores (core.md §5.2).
//
// The doctor app has to cope with three shapes of SOAP note ({s,o,a,p}, per
// section rows, and a flat «S: …» string) and reads them through a library. This
// app stores four columns and renders them directly; there is nothing to
// normalise, and adding a second shape later would be the defect, not the
// migration.
//
// ── Four labels and four captions ────────────────────────────────────────
//
// «شرح حال · معاینه و یافته‌ها · ارزیابی · برنامه» are the doctor app's captions,
// kept word for word (core.md §7.5) so a student who moves to the doctor app
// reads the same four prompts. The letter stays beside the caption because that
// is what a teacher says out loud: "put that in your O".
//
// ── The save is invisible and that is the point ──────────────────────────
//
// There is no «ذخیره» button here. `useEncounter` debounces 800 ms and sends only
// the changed field; the header's indicator is the whole of the feedback. What
// this component must never do is re-render the textarea from a value the parent
// re-derived — the state IS the parent's, one object, and typing in one field
// leaves the other three untouched.

export default function SoapEditor({
  soap,
  onChange,
  readOnly = false,
  onSuggest,
  suggestCost,
  suggesting = false,
}) {
  const { t } = useTranslation();

  // Dictation APPENDS. A student dictating into a field they already typed into
  // is adding a sentence, not replacing a paragraph.
  const dictation = useDictation((field, chunk) => {
    const current = soap?.[field] || '';
    onChange(field, current ? `${current} ${chunk}` : chunk);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="soap-editor">
      {!readOnly && onSuggest && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button
            size="sm"
            variant="outline"
            onClick={onSuggest}
            disabled={suggesting}
            data-testid="soap-suggest"
          >
            <Sparkles size={14} aria-hidden style={{ marginInlineEnd: 6 }} />
            {suggesting ? t('visit.soap.suggesting') : t('visit.soap.suggest')}
          </Button>
          {/* The price rides ON the control that spends it (core.md §3.5). */}
          <CostChip cost={suggestCost} before />
          <span style={{ fontSize: 11.5, color: CT.mute }}>{t('visit.soap.suggestNote')}</span>
        </div>
      )}

      {SOAP_SECTIONS.map((section) => {
        const listening = dictation.listening === section.id;
        return (
          <Card key={section.id} style={{ padding: 12 }} data-testid={`soap-field-${section.id}`}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <span style={{
                fontSize: 13, fontWeight: 800, color: C.teal,
                width: 18, textAlign: 'center',
              }}
              >
                {section.id.toUpperCase()}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                {t(section.labelKey)}
              </span>
              <span style={{ fontSize: 11.5, color: CT.mute }}>{t(section.captionKey)}</span>
              {!readOnly && dictation.supported && (
                <Button
                  size="sm"
                  variant={listening ? 'destructive' : 'ghost'}
                  onClick={() => dictation.toggle(section.id)}
                  style={{ marginInlineStart: 'auto' }}
                  data-testid={`soap-dictate-${section.id}`}
                  aria-label={t(listening ? 'visit.soap.dictateStop' : 'visit.soap.dictate')}
                >
                  {listening ? <MicOff size={14} aria-hidden /> : <Mic size={14} aria-hidden />}
                </Button>
              )}
            </div>
            <Textarea
              value={soap?.[section.id] || ''}
              onChange={(e) => onChange(section.id, e.target.value)}
              rows={5}
              disabled={readOnly}
              placeholder={readOnly ? '' : t(section.captionKey)}
              data-testid={`soap-input-${section.id}`}
              aria-label={t(section.labelKey)}
            />
            {listening && (
              <p style={{ margin: '6px 0 0', fontSize: 11.5, color: C.teal }}>
                {t('visit.soap.listening')}
              </p>
            )}
          </Card>
        );
      })}
    </div>
  );
}
